<script setup lang="ts">
  import { computed } from 'vue'
  import { useModalStack } from '@gat-ship/vue-components'
  import AgencyProvider from './AgencyProvider.vue'
  import AgencyStructureProvider from './AgencyStructureProvider.vue'
  import AgencyDetailModal from '../modals/AgencyDetailModal.vue'
  import {
    toValidAgencySection,
    type AgencyDetailSection
  } from '../detail-modal/agency-detail-modal-sections'

  // Open-state, the agency id, and active section come from this modal's entry
  // in the central ordered stack (the `?modals=` codec owns the URL).
  const KIND = 'agency'
  const stack = useModalStack()
  const entry = computed(() =>
    stack.entries.value.find((item) => item.kind === KIND)
  )

  function updateParams(patch: Record<string, string>): void {
    const current = entry.value
    if (!current) return
    stack.update(KIND, { ...current.params, ...patch })
  }

  const open = computed({
    get: () => entry.value != null,
    set: (value) => {
      if (!value) stack.close(KIND)
    }
  })
  const agencyId = computed(() => entry.value?.params.id)
  const activeSection = computed<AgencyDetailSection>({
    get: () => toValidAgencySection(entry.value?.params.agencySection ?? null),
    set: (section) => updateParams({ agencySection: section })
  })

  // Switch the modal to another agency picked from the sidebar select.
  function handleSelect(id: string) {
    updateParams({ id })
  }
</script>

<template>
  <!-- The structure tree (not fetched until needed) backs the agency move
       modal's group picker and lets agency mutations refresh the tree, just
       like the routed agency layout's ancestor structure provider. -->
  <AgencyStructureProvider>
    <AgencyProvider :agency-id="agencyId">
      <AgencyDetailModal
        v-model:open="open"
        v-model:section="activeSection"
        :agency-id="agencyId"
        @select="handleSelect"
      />
    </AgencyProvider>
  </AgencyStructureProvider>
</template>
